// Everything about the money on one sale, in one place, used by the sale
// popup (SalePreviewModal) wherever it opens: Sales, Receivable, Brokers…
//
// • useSaleMoney: loads the sale's customer payments, broker payments,
//   advances used on it and credit notes against it (live).
// • SaleMoneyStrip: Total · Received · Credited · Outstanding · Due, said once.
// • SalePaymentsTab: one timeline (invoice raised, payments, advances used,
//   credit notes) with a running balance; Edit / Delete a payment here.
// • SaleBrokersTab: one row per broker (%, commission, paid, owed, Pay)
//   with that broker's payments under it; Edit / Delete a payout here.
//
// The edit / delete transactions are the ones Receivable and Brokers used
// to carry themselves (bank balance, bank row, due date group and ledger
// entry all move together).

import { Fragment, useMemo, useState, type ReactNode } from 'react';
import { collection, doc, runTransaction } from 'firebase/firestore';
import { getClientDb } from '../firebase/clientApp';
import { useAuth } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import { canAccessPage } from '../utils/pages';
import { useSettings } from '../hooks/useSettings';
import { fmtMoney } from '../utils/currency';
import { buildEntry, postEntry, entryId, linesArPayment, linesBrokerPayment, linesSaleSettle } from '../utils/ledger';
import { shiftDueGroupPaid } from '../utils/dueGroups';
import { shortInvoiceNo } from '../utils/invoiceNo';
import { logActivity } from '../utils/activity';
import { fmtInInvoiceCurrency } from '../utils/invoiceCurrency';
import { Button, Modal, ConfirmModal, FormField } from './ui';
import RecordBrokerPaymentModal from './RecordBrokerPaymentModal';
import type { SaleInvoice, SalePayment, BrokerPayment, BankAccount, PaymentStatus } from '../types';
import { returnCreditOf } from '../utils/tradingSystem';
import { fmtDay, saleOutstanding, saleBrokerRows, settleLimit, type SaleMoneyData, type SaleBrokerRow, type SaleSettle } from '../utils/saleMoney';

// ── Helpers ───────────────────────────────────────────────────────────────

function todayISO() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}
function daysFromToday(dateStr: string): number {
  const today = new Date(); today.setHours(0, 0, 0, 0);
  return Math.floor((today.getTime() - new Date(dateStr + 'T00:00:00').getTime()) / 86_400_000);
}
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const payAmt = (p: SalePayment) => p.amount ?? (p as any).amountUSD ?? 0;
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const payAccName = (p: { bankAccountName?: string }) => p.bankAccountName ?? (p as any).paymentMethod ?? '';
const round2 = (n: number) => Math.round(n * 100) / 100;




// ── Figures strip (said once) ─────────────────────────────────────────────

export function SaleMoneyStrip({ invoice }: { invoice: SaleInvoice }) {
  const settings = useSettings();
  const money = (v: number) => fmtMoney(v, settings.currency);
  const paid = invoice.paidUSD ?? 0;
  const credited = invoice.creditedUSD ?? 0;
  const owed = saleOutstanding(invoice);
  const isPaid = invoice.paymentStatus === 'paid' || owed < 0.005;
  const separate = invoice.dueDateMode === 'separate' && (invoice.dueGroups?.length ?? 0) > 0;
  // Next unpaid due date (several in 'separate' mode).
  const nextDue = separate
    ? invoice.dueGroups!.filter(g => g.paymentStatus !== 'paid').map(g => g.dueDate).sort()[0] ?? invoice.dueDate
    : invoice.dueDate;
  const days = nextDue ? daysFromToday(nextDue) : 0;
  const dueNote = isPaid ? 'Paid in full'
    : days > 0 ? `${days} day${days === 1 ? '' : 's'} overdue`
    : days === 0 ? 'Due today' : `In ${-days} day${days === -1 ? '' : 's'}`;
  const dueTone = isPaid ? 'var(--success)' : days > 0 ? 'var(--danger)' : days >= -7 ? 'var(--warning)' : 'var(--text-3)';

  const cells: { label: string; value: ReactNode; note?: ReactNode; color?: string; strong?: boolean }[] = [
    { label: 'Invoice Total', value: money(invoice.totalAmountUSD), note: fmtInInvoiceCurrency(invoice.totalAmountUSD, invoice, settings.currency) ?? undefined },
    { label: 'Received', value: money(paid), color: paid > 0 ? 'var(--success)' : undefined },
    ...(credited > 0.005 ? [{ label: 'Credited', value: money(credited), note: 'Credit notes' }] : []),
    { label: 'Outstanding', value: money(owed), strong: true, color: owed > 0.005 ? 'var(--danger)' : 'var(--text-3)', note: fmtInInvoiceCurrency(owed, invoice, settings.currency) ?? undefined },
    {
      label: separate ? 'Next Due' : 'Due Date',
      value: fmtDay(nextDue),
      note: <span style={{ color: dueTone, fontWeight: 700 }}>{dueNote}{separate ? ` · ${invoice.dueGroups!.length} dates` : ''}</span>,
    },
  ];
  return (
    <div className="sm-strip">
      {cells.map(c => (
        <div key={c.label} className="sm-strip-cell">
          <div className="sm-strip-label">{c.label}</div>
          <div className={`sm-strip-value${c.strong ? ' strong' : ''}`} style={c.color ? { color: c.color } : undefined}>{c.value}</div>
          {c.note && <div className="sm-strip-note">{c.note}</div>}
        </div>
      ))}
    </div>
  );
}

// ── Shared edit form for one payment (customer or broker) ─────────────────

interface EditForm { amount: string; paidDate: string; referenceNo: string; notes: string }

function EditPaymentModal({ open, title, subtitle, currency, form, setForm, saving, error, onSave, onClose }: {
  open: boolean; title: string; subtitle: string; currency: string;
  form: EditForm; setForm: (f: EditForm) => void;
  saving: boolean; error: string; onSave: () => void; onClose: () => void;
}) {
  return (
    <Modal
      open={open} onClose={onClose} title={title} subtitle={subtitle} size="sm"
      footer={
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8, width: '100%' }}>
          {error && <div className="msg msg-error" style={{ margin: 0 }}>{error}</div>}
          <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
            <Button variant="secondary" onClick={onClose} disabled={saving}>Cancel</Button>
            <Button variant="primary" onClick={onSave} loading={saving}>Save Changes</Button>
          </div>
        </div>
      }
    >
      <div className="form-row-2">
        <FormField label={`Amount (${currency})`} required>
          <input type="number" step="0.01" min="0.01" value={form.amount} autoFocus
            onChange={e => setForm({ ...form, amount: e.target.value })} />
        </FormField>
        <FormField label="Payment Date" required>
          <input type="date" value={form.paidDate} onChange={e => setForm({ ...form, paidDate: e.target.value })} />
        </FormField>
      </div>
      <FormField label="Reference No.">
        <input value={form.referenceNo} onChange={e => setForm({ ...form, referenceNo: e.target.value })}
          placeholder="Bank ref, cheque no., etc." style={{ fontFamily: 'var(--font-num)' }} />
      </FormField>
      <FormField label="Notes (optional)">
        <textarea value={form.notes} onChange={e => setForm({ ...form, notes: e.target.value })} style={{ minHeight: 52 }} />
      </FormField>
    </Modal>
  );
}

/** The bank account a payment went through, as a link to its row on the Bank page. */
function BankLink({ name, txId }: { name: string; txId?: string }) {
  const navigate = useNavigate();
  const { user } = useAuth();
  if (!name) return null;
  if (!txId || !canAccessPage(user, 'bank')) return <>{name}</>;
  return (
    <button type="button" className="sm-link" title="Open this payment on the Bank page"
      onClick={() => navigate(`/bank?tx=${txId}`)}>{name}</button>
  );
}

function RowActions({ onEdit, onDelete }: { onEdit: () => void; onDelete: () => void }) {
  return (
    <span className="sm-row-actions">
      <button type="button" className="sm-act" onClick={onEdit}>Edit</button>
      <button type="button" className="sm-act danger" onClick={onDelete}>Delete</button>
    </span>
  );
}

// ── Payments tab: one timeline with a running balance ─────────────────────

interface TimelineRow {
  key: string; date: string; order: number;
  what: string; ref?: string; detail: ReactNode; amount: number;
  payment?: SalePayment;
  settle?: SaleSettle;
}

export function SalePaymentsTab({ invoice, data, canEdit, highlightBankTxId }: {
  invoice: SaleInvoice; data: SaleMoneyData;
  /** Edit / Delete a payment (Receivable and Sales; not read only views). */
  canEdit: boolean;
  /** The bank row this was opened from: its payment is picked out. */
  highlightBankTxId?: string;
}) {
  const { user, business } = useAuth();
  const settings = useSettings();
  const money = (v: number) => fmtMoney(v, settings.currency);

  const groupDate = useMemo(() => new Map((invoice.dueGroups ?? []).map(g => [g.id, g.dueDate])), [invoice.dueGroups]);

  const rows: TimelineRow[] = useMemo(() => {
    const out: TimelineRow[] = [{
      key: 'inv', date: invoice.saleDate, order: 0, what: 'Invoice raised', ref: invoice.invoiceNo,
      detail: `${invoice.lineItems?.length ?? 0} stone${(invoice.lineItems?.length ?? 0) === 1 ? '' : 's'}${(invoice.chargesUSD ?? 0) > 0 ? ' + charges' : ''}`,
      amount: invoice.totalAmountUSD,
    }];
    for (const p of data.payments) {
      const bits = [p.referenceNo ? `Ref ${p.referenceNo}` : '', p.dueGroupId && groupDate.get(p.dueGroupId) ? `For due ${fmtDay(groupDate.get(p.dueGroupId))}` : '']
        .filter(Boolean).join(' · ');
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const btId = (p as any).bankTransactionId as string | undefined;
      out.push({
        key: `p-${p.id}`, date: p.paidDate, order: 1, what: 'Payment received',
        detail: <>
          <BankLink name={payAccName(p)} txId={btId} />
          {bits && <>{payAccName(p) ? ' · ' : ''}{bits}</>}
          {p.notes && <div className="sm-sub">{p.notes}</div>}
        </>,
        amount: -payAmt(p), payment: p,
      });
    }
    for (const a of data.advanceUses) {
      out.push({ key: `a-${a.id}`, date: a.date, order: 2, what: 'Advance used', ref: a.advanceNo, detail: 'Taken from the customer\'s advance', amount: -a.amountUSD });
    }
    for (const c of data.credits) {
      out.push({
        key: `c-${c.id}`, date: c.date, order: 3, what: 'Credit note', ref: c.returnNo,
        detail: `${c.lines?.length ?? 0} stone${(c.lines?.length ?? 0) === 1 ? '' : 's'} returned · ${(c.totalCarat ?? 0).toFixed(2)} ct`,
        amount: -returnCreditOf(c.totalRevenueUSD, c),
      });
    }
    for (const st of data.settles) {
      out.push({
        key: `s-${st.id}`, date: st.date, order: 4, what: 'Settled difference',
        detail: 'Small amount left after payment, written off as FX Loss',
        amount: -(st.amountUSD ?? 0), settle: st,
      });
    }
    return out.sort((x, y) => (x.date ?? '').localeCompare(y.date ?? '') || x.order - y.order);
  }, [invoice, data, groupDate]);

  const withBalance = rows.reduce<(TimelineRow & { balance: number })[]>((acc, r) => {
    const prev = acc.length ? acc[acc.length - 1].balance : 0;
    acc.push({ ...r, balance: round2(prev + r.amount) });
    return acc;
  }, []);
  const running = withBalance.length ? withBalance[withBalance.length - 1].balance : 0;
  // Shown newest first; each row's balance is still what was left after it.
  const shownRows = [...withBalance].reverse();

  // ── Edit / delete a customer payment ──
  const [editing, setEditing] = useState<SalePayment | null>(null);
  const [form, setForm] = useState<EditForm>({ amount: '', paidDate: '', referenceNo: '', notes: '' });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [deleting, setDeleting] = useState<SalePayment | null>(null);
  const [delSaving, setDelSaving] = useState(false);
  const [delError, setDelError] = useState('');

  function openEdit(p: SalePayment) {
    setEditing(p);
    setForm({ amount: String(payAmt(p)), paidDate: p.paidDate ?? todayISO(), referenceNo: p.referenceNo ?? '', notes: p.notes ?? '' });
    setError('');
  }

  async function saveEdit() {
    if (!editing || !business) return;
    const newAmt = parseFloat(form.amount);
    if (isNaN(newAmt) || newAmt <= 0) { setError('Enter a valid amount.'); return; }
    if (!form.paidDate) { setError('Select a date.'); return; }
    setSaving(true); setError('');
    const db = getClientDb();
    if (!db) { setError('Database not connected.'); setSaving(false); return; }

    const oldAmt = payAmt(editing);
    const diff = newAmt - oldAmt;
    const newPaid = Math.max(0, (invoice.paidUSD ?? 0) + diff);
    const newStatus: PaymentStatus =
      newPaid + (invoice.creditedUSD ?? 0) >= invoice.totalAmountUSD - 0.005 ? 'paid' : newPaid > 0 ? 'partial' : 'unpaid';
    // A converted receipt moves its bank account by its own currency's amount.
    const newBankAmt = editing.bankCurrency && editing.exchangeRate ? newAmt * editing.exchangeRate : newAmt;
    const bankDiff = newBankAmt - (editing.bankAmount ?? oldAmt);

    try {
      await runTransaction(db, async tx => {
        const payRef = doc(db, 'sales', invoice.id, 'payments', editing.id);
        const invRef = doc(db, 'sales', invoice.id);
        const accRef = editing.bankAccountId && bankDiff !== 0 ? doc(db, 'bankAccounts', editing.bankAccountId) : null;
        const accSnap = accRef ? await tx.get(accRef) : null;
        const groupSnap = editing.dueGroupId ? await tx.get(invRef) : null;
        const groupUpdate = groupSnap?.exists()
          ? shiftDueGroupPaid((groupSnap.data() as SaleInvoice).dueGroups ?? [], editing.dueGroupId!, diff)
          : null;

        tx.update(payRef, {
          amount: newAmt, bankAmount: editing.bankCurrency ? newBankAmt : null, paidDate: form.paidDate,
          referenceNo: form.referenceNo.trim() || null, notes: form.notes.trim() || null,
        });
        logActivity(tx, db, business.id, user, {
          module: 'receipt', action: 'edit', recordId: invoice.id, recordNo: invoice.invoiceNo, partyId: invoice.toEntityId,
          amountUSD: newAmt, prevAmountUSD: oldAmt, note: payAccName(editing),
        });
        tx.update(invRef, groupUpdate
          ? { ...groupUpdate, updatedAt: new Date().toISOString() }
          : { paidUSD: newPaid, paymentStatus: newStatus, updatedAt: new Date().toISOString() });
        if (accRef && accSnap?.exists()) {
          tx.update(accRef, { currentBalance: (accSnap.data() as BankAccount).currentBalance + bankDiff });
        }
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const btId = (editing as any).bankTransactionId as string | undefined;
        if (btId && bankDiff !== 0) tx.update(doc(db, 'bankTransactions', btId), { amount: newBankAmt });
        if (editing.bankAccountId) {
          postEntry(tx, db, buildEntry({
            businessId: business.id, sourceType: 'ar_payment', sourceId: editing.id,
            sourceCollection: `sales/${invoice.id}/payments`,
            date: form.paidDate, currency: editing.currency ?? 'USD',
            createdBy: user?.id ?? editing.createdBy ?? 'system',
            note: `Receipt for ${invoice.invoiceNo}`,
            lines: linesArPayment(newAmt, invoice.toEntityId, editing.bankAccountId),
          }), entryId('ar_payment', editing.id));
        }
      });
      setEditing(null);
    } catch (e) { setError(e instanceof Error ? e.message : 'Failed to save.'); }
    setSaving(false);
  }

  async function confirmDelete() {
    if (!deleting || !business) return;
    setDelSaving(true); setDelError('');
    const db = getClientDb();
    if (!db) { setDelError('Database not connected.'); setDelSaving(false); return; }
    const amt = payAmt(deleting);
    const bankAmt = deleting.bankAmount ?? amt;
    const newPaid = Math.max(0, (invoice.paidUSD ?? 0) - amt);
    const newStatus: PaymentStatus =
      newPaid + (invoice.creditedUSD ?? 0) >= invoice.totalAmountUSD - 0.005 ? 'paid' : newPaid > 0 ? 'partial' : 'unpaid';
    try {
      await runTransaction(db, async tx => {
        const payRef = doc(db, 'sales', invoice.id, 'payments', deleting.id);
        const invRef = doc(db, 'sales', invoice.id);
        const accRef = deleting.bankAccountId ? doc(db, 'bankAccounts', deleting.bankAccountId) : null;
        const accSnap = accRef ? await tx.get(accRef) : null;
        const groupSnap = deleting.dueGroupId ? await tx.get(invRef) : null;
        const groupUpdate = groupSnap?.exists()
          ? shiftDueGroupPaid((groupSnap.data() as SaleInvoice).dueGroups ?? [], deleting.dueGroupId!, -amt)
          : null;
        tx.delete(payRef);
        logActivity(tx, db, business.id, user, {
          module: 'receipt', action: 'delete', recordId: invoice.id, recordNo: invoice.invoiceNo, partyId: invoice.toEntityId,
          amountUSD: amt, note: payAccName(deleting),
        });
        tx.update(invRef, groupUpdate
          ? { ...groupUpdate, updatedAt: new Date().toISOString() }
          : { paidUSD: newPaid, paymentStatus: newStatus, updatedAt: new Date().toISOString() });
        if (accRef && accSnap?.exists()) {
          tx.update(accRef, { currentBalance: (accSnap.data() as BankAccount).currentBalance - bankAmt });
        }
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const btId = (deleting as any).bankTransactionId as string | undefined;
        if (btId) tx.delete(doc(db, 'bankTransactions', btId));
        tx.delete(doc(db, 'journalEntries', entryId('ar_payment', deleting.id)));
      });
      setDeleting(null);
    } catch (e) { setDelError(e instanceof Error ? e.message : 'Failed to delete.'); }
    setDelSaving(false);
  }

  // ── Settle difference: close a small leftover (bank rate, rounding, fee)
  // as FX Loss. Only for an amount up to settleLimit (1% or 50). ──
  const owedNow = saleOutstanding(invoice);
  const canSettle = canEdit && owedNow > 0.005 && owedNow <= settleLimit(invoice) + 0.005 && (invoice.paidUSD ?? 0) > 0.005;
  const [settleOpen, setSettleOpen] = useState(false);
  const [settleBusy, setSettleBusy] = useState(false);
  const [settleError, setSettleError] = useState('');
  const [unsettle, setUnsettle] = useState<SaleSettle | null>(null);

  const statusOf = (paid: number, credited: number, total: number): PaymentStatus =>
    paid + credited >= total - 0.005 ? 'paid' : paid > 0.005 ? 'partial' : 'unpaid';

  async function confirmSettle() {
    if (!business) return;
    setSettleBusy(true); setSettleError('');
    const db = getClientDb();
    if (!db) { setSettleError('Database not connected.'); setSettleBusy(false); return; }
    try {
      await runTransaction(db, async tx => {
        const invRef = doc(db, 'sales', invoice.id);
        const snap = await tx.get(invRef);
        if (!snap.exists()) throw new Error('That sale no longer exists.');
        const cur = snap.data() as SaleInvoice;
        const amt = round2(saleOutstanding({ ...cur, id: invoice.id }));
        if (amt <= 0.005) throw new Error('Nothing is left to settle.');
        if (amt > settleLimit(cur) + 0.005) throw new Error('The amount left is more than a small difference now.');
        // A sale with several due dates: put it on the first one still open.
        const groups = cur.dueDateMode === 'separate' ? (cur.dueGroups ?? []) : [];
        const gi = groups.findIndex(g => g.amountUSD - g.paidUSD - g.creditedUSD > 0.005);
        const newGroups = gi < 0 ? null : groups.map((g, i) => i !== gi ? g : {
          ...g, creditedUSD: round2(g.creditedUSD + amt),
          paymentStatus: statusOf(g.paidUSD, g.creditedUSD + amt, g.amountUSD),
        });
        const adjRef = doc(collection(db, 'sales', invoice.id, 'adjustments'));
        const date = todayISO();
        tx.set(adjRef, {
          kind: 'settle', date, amountUSD: amt, dueGroupId: gi < 0 ? null : groups[gi].id,
          createdAt: new Date().toISOString(), createdBy: user?.id ?? '',
        });
        const credited = round2((cur.creditedUSD ?? 0) + amt);
        tx.update(invRef, {
          creditedUSD: credited,
          paymentStatus: statusOf(cur.paidUSD ?? 0, credited, cur.totalAmountUSD),
          ...(newGroups ? { dueGroups: newGroups } : {}),
          updatedAt: new Date().toISOString(),
        });
        postEntry(tx, db, buildEntry({
          businessId: business.id, sourceType: 'sale_settle', sourceId: adjRef.id,
          sourceCollection: `sales/${invoice.id}/adjustments`, date, currency: settings.currency,
          createdBy: user?.id ?? '', note: `Settled difference on ${invoice.invoiceNo}`,
          lines: linesSaleSettle(amt, invoice.toEntityId),
        }), entryId('sale_settle', adjRef.id));
        logActivity(tx, db, business.id, user, {
          module: 'receipt', action: 'edit', recordId: invoice.id, recordNo: invoice.invoiceNo, partyId: invoice.toEntityId,
          amountUSD: amt, note: 'Settled difference (FX Loss)',
        });
      });
      setSettleOpen(false);
    } catch (e) { setSettleError(e instanceof Error ? e.message : 'Could not settle.'); }
    setSettleBusy(false);
  }

  async function confirmUnsettle() {
    if (!unsettle || !business) return;
    setSettleBusy(true); setSettleError('');
    const db = getClientDb();
    if (!db) { setSettleError('Database not connected.'); setSettleBusy(false); return; }
    try {
      await runTransaction(db, async tx => {
        const invRef = doc(db, 'sales', invoice.id);
        const snap = await tx.get(invRef);
        if (!snap.exists()) throw new Error('That sale no longer exists.');
        const cur = snap.data() as SaleInvoice;
        const amt = unsettle.amountUSD ?? 0;
        const groups = cur.dueGroups ?? [];
        const newGroups = unsettle.dueGroupId ? groups.map(g => g.id !== unsettle.dueGroupId ? g : {
          ...g, creditedUSD: round2(Math.max(0, g.creditedUSD - amt)),
          paymentStatus: statusOf(g.paidUSD, Math.max(0, g.creditedUSD - amt), g.amountUSD),
        }) : null;
        const credited = round2(Math.max(0, (cur.creditedUSD ?? 0) - amt));
        tx.delete(doc(db, 'sales', invoice.id, 'adjustments', unsettle.id));
        tx.update(invRef, {
          creditedUSD: credited,
          paymentStatus: statusOf(cur.paidUSD ?? 0, credited, cur.totalAmountUSD),
          ...(newGroups ? { dueGroups: newGroups } : {}),
          updatedAt: new Date().toISOString(),
        });
        tx.delete(doc(db, 'journalEntries', entryId('sale_settle', unsettle.id)));
        logActivity(tx, db, business.id, user, {
          module: 'receipt', action: 'delete', recordId: invoice.id, recordNo: invoice.invoiceNo, partyId: invoice.toEntityId,
          amountUSD: amt, note: 'Settled difference removed',
        });
      });
      setUnsettle(null);
    } catch (e) { setSettleError(e instanceof Error ? e.message : 'Could not remove it.'); }
    setSettleBusy(false);
  }

  const separate = invoice.dueDateMode === 'separate' && (invoice.dueGroups?.length ?? 0) > 0;

  return (
    <>
      {separate && (
        <div style={{ marginBottom: 16 }}>
          <div className="sm-title">Due Dates</div>
          <div className="table-wrap">
            <table>
              <thead><tr>
                <th>Due</th><th style={{ textAlign: 'right' }}>Amount</th><th style={{ textAlign: 'right' }}>Received</th>
                <th style={{ textAlign: 'right' }}>Outstanding</th><th>Status</th>
              </tr></thead>
              <tbody>
                {invoice.dueGroups!.map(g => {
                  const owed = Math.max(0, g.amountUSD - g.paidUSD - g.creditedUSD);
                  return (
                    <tr key={g.id}>
                      <td style={{ fontFamily: 'var(--font-num)' }}>{fmtDay(g.dueDate)}</td>
                      <td style={{ textAlign: 'right', fontFamily: 'var(--font-num)' }}>{money(g.amountUSD)}</td>
                      <td style={{ textAlign: 'right', fontFamily: 'var(--font-num)', color: g.paidUSD > 0 ? 'var(--success)' : undefined }}>{g.paidUSD > 0 ? money(g.paidUSD) : <span style={{ opacity: 0.4 }}>—</span>}</td>
                      <td style={{ textAlign: 'right', fontFamily: 'var(--font-num)', fontWeight: 700, color: owed > 0.005 ? 'var(--danger)' : 'var(--text-3)' }}>{money(owed)}</td>
                      <td><StatusPill status={g.paymentStatus} /></td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      <div className="sm-title">Money In and Out</div>
      {data.loading ? (
        <div className="skeleton" style={{ height: 80, borderRadius: 8 }} />
      ) : (
        <div className="table-wrap">
          <table className="sm-timeline">
            <thead><tr>
              <th style={{ width: 110 }}>Date</th><th>What</th><th>Details</th>
              <th style={{ textAlign: 'right' }}>Amount</th><th style={{ textAlign: 'right' }}>Balance</th>
              {canEdit && <th style={{ width: 120 }} />}
            </tr></thead>
            <tbody>
              {shownRows.map(r => (
                <tr key={r.key} className={r.payment && highlightBankTxId && (r.payment as { bankTransactionId?: string }).bankTransactionId === highlightBankTxId ? 'sm-hl' : undefined}>
                  <td style={{ fontFamily: 'var(--font-num)', whiteSpace: 'nowrap' }}>{fmtDay(r.date)}</td>
                  <td style={{ whiteSpace: 'nowrap' }}>
                    <span className={`sm-kind sm-kind-${r.key[0]}`}>{r.what}</span>
                    {r.ref && <span className="sm-ref" title={r.ref}>{shortInvoiceNo(r.ref)}</span>}
                  </td>
                  <td>{r.detail}</td>
                  <td style={{ textAlign: 'right', fontFamily: 'var(--font-num)', fontWeight: 600, whiteSpace: 'nowrap', color: r.amount < 0 ? 'var(--success)' : 'var(--text-1)' }}>
                    {r.amount < 0 ? `− ${money(-r.amount)}` : money(r.amount)}
                  </td>
                  <td style={{ textAlign: 'right', fontFamily: 'var(--font-num)', whiteSpace: 'nowrap' }}>{money(r.balance)}</td>
                  {canEdit && (
                    <td style={{ textAlign: 'right' }}>
                      {r.payment && <RowActions onEdit={() => openEdit(r.payment!)} onDelete={() => { setDeleting(r.payment!); setDelError(''); }} />}
                      {r.settle && <button type="button" className="sm-act danger" onClick={() => { setUnsettle(r.settle!); setSettleError(''); }}>Delete</button>}
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr>
                <td colSpan={4} style={{ fontWeight: 700, color: 'var(--text-1)' }}>
                  Outstanding now
                  {canSettle && (
                    <button type="button" className="sm-settle" onClick={() => { setSettleOpen(true); setSettleError(''); }}
                      title="Close this small leftover (bank rate, rounding or a bank fee) as FX Loss">
                      Settle difference
                    </button>
                  )}
                </td>
                <td style={{ textAlign: 'right', fontFamily: 'var(--font-num)', fontWeight: 800, color: running > 0.005 ? 'var(--danger)' : 'var(--text-3)' }}>{money(Math.max(0, running))}</td>
                {canEdit && <td />}
              </tr>
            </tfoot>
          </table>
        </div>
      )}

      <ConfirmModal
        open={settleOpen} onClose={() => setSettleOpen(false)} onConfirm={confirmSettle}
        title="Settle Difference"
        message={<>Close the last <strong>{money(owedNow)}</strong> on {invoice.invoiceNo}? It's booked as FX Loss and the invoice shows as paid. You can delete it later.</>}
        confirmLabel="Settle" loading={settleBusy} error={settleError}
      />
      <ConfirmModal
        open={!!unsettle} onClose={() => setUnsettle(null)} onConfirm={confirmUnsettle}
        title="Delete Settled Difference"
        message={unsettle ? <>Put <strong>{money(unsettle.amountUSD)}</strong> back as owed on {invoice.invoiceNo}?</> : null}
        confirmLabel="Delete" loading={settleBusy} error={settleError}
      />
      <EditPaymentModal
        open={!!editing} title="Edit Payment"
        subtitle={editing ? `${payAccName(editing)} · ${fmtDay(editing.paidDate)}` : ''}
        currency={editing?.currency ?? settings.currency}
        form={form} setForm={setForm} saving={saving} error={error}
        onSave={saveEdit} onClose={() => setEditing(null)}
      />
      <ConfirmModal
        open={!!deleting} onClose={() => setDeleting(null)} onConfirm={confirmDelete}
        title="Delete Payment"
        message={deleting ? <>Delete the payment of <strong>{money(payAmt(deleting))}</strong> on {fmtDay(deleting.paidDate)}? The invoice balance and the bank deposit go back.</> : null}
        confirmLabel="Delete Payment" loading={delSaving} error={delError}
      />
    </>
  );
}

function StatusPill({ status }: { status: PaymentStatus }) {
  const c = status === 'paid' ? 'var(--success)' : status === 'partial' ? 'var(--warning)' : 'var(--danger)';
  return (
    <span className="badge" style={{ color: c, background: `color-mix(in srgb, ${c} 12%, transparent)`, border: `1px solid color-mix(in srgb, ${c} 30%, transparent)` }}>
      {status === 'paid' ? 'Paid' : status === 'partial' ? 'Partly paid' : 'Unpaid'}
    </span>
  );
}

// ── Brokers tab ───────────────────────────────────────────────────────────

export function SaleBrokersTab({ invoice, data, entityName, canPay, highlightBrokerId, highlightBankTxId }: {
  invoice: SaleInvoice; data: SaleMoneyData;
  entityName: (id: string) => string;
  /** Pay / Edit / Delete broker payouts (Brokers page access). */
  canPay: boolean;
  /** The broker the popup was opened for (from the Brokers page). */
  highlightBrokerId?: string;
  /** The bank row this was opened from: its payout is picked out. */
  highlightBankTxId?: string;
}) {
  const { user, business } = useAuth();
  const settings = useSettings();
  const money = (v: number) => fmtMoney(v, settings.currency);
  const brokers = useMemo(() => saleBrokerRows(invoice), [invoice]);
  const paysFor = (r: SaleBrokerRow) => data.brokerPayments
    .filter(p => (r.line ? p.brokerEntityId === r.brokerEntityId : !p.brokerEntityId))
    .sort((a, b) => (b.paidDate ?? '').localeCompare(a.paidDate ?? ''));

  const [payFor, setPayFor] = useState<{ row: SaleBrokerRow; paid: number } | null>(null);
  const [editing, setEditing] = useState<{ pay: BrokerPayment; row: SaleBrokerRow } | null>(null);
  const [form, setForm] = useState<EditForm>({ amount: '', paidDate: '', referenceNo: '', notes: '' });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [deleting, setDeleting] = useState<{ pay: BrokerPayment; row: SaleBrokerRow } | null>(null);
  const [delSaving, setDelSaving] = useState(false);
  const [delError, setDelError] = useState('');

  function openEdit(pay: BrokerPayment, row: SaleBrokerRow) {
    setEditing({ pay, row });
    setForm({ amount: String(pay.amount ?? 0), paidDate: pay.paidDate ?? todayISO(), referenceNo: pay.referenceNo ?? '', notes: pay.notes ?? '' });
    setError('');
  }

  // The header broker's paid / status live on the sale; a line broker's are
  // always summed from its payments, so nothing on the sale moves for it.
  const headerStatus = (paid: number): PaymentStatus =>
    paid >= (invoice.brokerCommissionUSD ?? 0) - 0.005 ? 'paid' : paid > 0 ? 'partial' : 'unpaid';

  async function saveEdit() {
    if (!editing || !business) return;
    const { pay, row } = editing;
    const newAmt = parseFloat(form.amount);
    if ((pay.gstTotalUSD ?? 0) > 0) { setError('This payout has GST on it. Delete it and record it again to change it.'); return; }
    if (isNaN(newAmt) || newAmt <= 0) { setError('Enter a valid amount.'); return; }
    if (!form.paidDate) { setError('Select a date.'); return; }
    setSaving(true); setError('');
    const db = getClientDb();
    if (!db) { setError('Database not connected.'); setSaving(false); return; }
    const oldAmt = pay.amount ?? 0;
    const diff = newAmt - oldAmt;
    const newPaid = Math.max(0, (invoice.brokerPaidUSD ?? 0) + diff);
    const newBankAmt = pay.exchangeRate ? newAmt * pay.exchangeRate : newAmt;
    const bankDiff = newBankAmt - (pay.bankAmount ?? oldAmt);
    try {
      await runTransaction(db, async tx => {
        const payRef = doc(db, 'sales', invoice.id, 'brokerPayments', pay.id);
        const accRef = pay.bankAccountId && bankDiff !== 0 ? doc(db, 'bankAccounts', pay.bankAccountId) : null;
        const accSnap = accRef ? await tx.get(accRef) : null;
        tx.update(payRef, {
          amount: newAmt, bankAmount: pay.bankCurrency ? newBankAmt : null, paidDate: form.paidDate,
          referenceNo: form.referenceNo.trim() || null, notes: form.notes.trim() || null,
        });
        logActivity(tx, db, business.id, user, {
          module: 'broker', action: 'edit', recordId: invoice.id, recordNo: invoice.invoiceNo, partyId: row.brokerEntityId,
          amountUSD: newAmt, prevAmountUSD: oldAmt, note: payAccName(pay),
        });
        if (!row.line) {
          tx.update(doc(db, 'sales', invoice.id), { brokerPaidUSD: newPaid, brokerPaymentStatus: headerStatus(newPaid), updatedAt: new Date().toISOString() });
        }
        if (accRef && accSnap?.exists()) {
          tx.update(accRef, { currentBalance: (accSnap.data() as BankAccount).currentBalance - bankDiff });
        }
        if (pay.bankTransactionId && bankDiff !== 0) {
          tx.update(doc(db, 'bankTransactions', pay.bankTransactionId), { amount: newBankAmt });
        }
        if (pay.bankAccountId) {
          postEntry(tx, db, buildEntry({
            businessId: business.id, sourceType: 'broker_payment', sourceId: pay.id,
            sourceCollection: `sales/${invoice.id}/brokerPayments`,
            date: form.paidDate, currency: pay.currency ?? 'USD',
            createdBy: user?.id ?? pay.createdBy ?? 'system',
            note: `Broker payout — ${invoice.invoiceNo}`,
            lines: linesBrokerPayment(newAmt, row.brokerEntityId, pay.bankAccountId),
          }), entryId('broker_payment', pay.id));
        }
      });
      setEditing(null);
    } catch (e) { setError(e instanceof Error ? e.message : 'Failed to save.'); }
    setSaving(false);
  }

  async function confirmDelete() {
    if (!deleting || !business) return;
    const { pay, row } = deleting;
    setDelSaving(true); setDelError('');
    const db = getClientDb();
    if (!db) { setDelError('Database not connected.'); setDelSaving(false); return; }
    const amt = pay.amount ?? 0;
    const bankAmt = pay.bankAmount ?? amt;
    const newPaid = Math.max(0, (invoice.brokerPaidUSD ?? 0) - amt);
    try {
      await runTransaction(db, async tx => {
        const accRef = pay.bankAccountId ? doc(db, 'bankAccounts', pay.bankAccountId) : null;
        const accSnap = accRef ? await tx.get(accRef) : null;
        tx.delete(doc(db, 'sales', invoice.id, 'brokerPayments', pay.id));
        logActivity(tx, db, business.id, user, {
          module: 'broker', action: 'delete', recordId: invoice.id, recordNo: invoice.invoiceNo, partyId: row.brokerEntityId,
          amountUSD: amt, note: payAccName(pay),
        });
        if (!row.line) {
          tx.update(doc(db, 'sales', invoice.id), { brokerPaidUSD: newPaid, brokerPaymentStatus: headerStatus(newPaid), updatedAt: new Date().toISOString() });
        }
        if (accRef && accSnap?.exists()) {
          tx.update(accRef, { currentBalance: (accSnap.data() as BankAccount).currentBalance + bankAmt });
        }
        if (pay.bankTransactionId) tx.delete(doc(db, 'bankTransactions', pay.bankTransactionId));
        tx.delete(doc(db, 'journalEntries', entryId('broker_payment', pay.id)));
      });
      setDeleting(null);
    } catch (e) { setDelError(e instanceof Error ? e.message : 'Failed to delete.'); }
    setDelSaving(false);
  }

  if (brokers.length === 0) {
    return <div className="sm-empty">No broker on this sale.</div>;
  }

  const totals = brokers.reduce((t, r) => {
    const paid = paysFor(r).reduce((s, p) => s + (p.amount ?? 0), 0);
    return { commission: t.commission + r.commission, paid: t.paid + paid };
  }, { commission: 0, paid: 0 });

  return (
    <>
      <div className="table-wrap">
        <table className="sm-timeline">
          <thead><tr>
            <th>Broker</th><th style={{ textAlign: 'right' }}>%</th>
            <th style={{ textAlign: 'right' }}>Commission</th><th style={{ textAlign: 'right' }}>Paid</th>
            <th style={{ textAlign: 'right' }}>Owed</th><th style={{ width: 150 }} />
          </tr></thead>
          <tbody>
            {brokers.map(r => {
              const pays = paysFor(r);
              const paid = round2(pays.reduce((s, p) => s + (p.amount ?? 0), 0));
              const owed = Math.max(0, round2(r.commission - paid));
              return (
                <Fragment key={`${r.line ? 'l' : 'h'}-${r.brokerEntityId}`}>
                  <tr className={`sm-broker-row${r.brokerEntityId === highlightBrokerId ? ' sm-hl' : ''}`}>
                    <td style={{ fontWeight: 700, color: 'var(--text-1)' }}>{entityName(r.brokerEntityId)}</td>
                    <td style={{ textAlign: 'right', fontFamily: 'var(--font-num)' }}>{r.percent != null ? `${r.percent}%` : <span style={{ opacity: 0.4 }}>—</span>}</td>
                    <td style={{ textAlign: 'right', fontFamily: 'var(--font-num)' }}>{money(r.commission)}</td>
                    <td style={{ textAlign: 'right', fontFamily: 'var(--font-num)', color: paid > 0 ? 'var(--success)' : undefined }}>{paid > 0 ? money(paid) : <span style={{ opacity: 0.4 }}>—</span>}</td>
                    <td style={{ textAlign: 'right', fontFamily: 'var(--font-num)', fontWeight: 700, color: owed > 0.005 ? 'var(--danger)' : 'var(--text-3)' }}>{owed > 0.005 ? money(owed) : 'Paid'}</td>
                    <td style={{ textAlign: 'right' }}>
                      {canPay && owed > 0.005 && (
                        <Button variant="primary" size="sm" onClick={() => setPayFor({ row: r, paid })}>Pay {money(owed)}</Button>
                      )}
                    </td>
                  </tr>
                  {pays.map(p => (
                    <tr key={p.id} className={`sm-sub-row${highlightBankTxId && p.bankTransactionId === highlightBankTxId ? ' sm-hl' : ''}`}>
                      <td colSpan={3}>
                        <span className="sm-kind sm-kind-p">Paid out</span>
                        <span style={{ fontFamily: 'var(--font-num)' }}>{fmtDay(p.paidDate)}</span>
                        <span className="sm-sub" style={{ display: 'inline', marginLeft: 8 }}>
                          <BankLink name={payAccName(p)} txId={p.bankTransactionId} />
                          {p.referenceNo ? ` · Ref ${p.referenceNo}` : ''}
                        </span>
                      </td>
                      <td style={{ textAlign: 'right', fontFamily: 'var(--font-num)', color: 'var(--success)' }}>{money(p.amount ?? 0)}</td>
                      <td />
                      <td style={{ textAlign: 'right' }}>
                        {canPay && <RowActions onEdit={() => openEdit(p, r)} onDelete={() => { setDeleting({ pay: p, row: r }); setDelError(''); }} />}
                      </td>
                    </tr>
                  ))}
                </Fragment>
              );
            })}
          </tbody>
          {brokers.length > 1 && (
            <tfoot>
              <tr>
                <td colSpan={2} style={{ fontWeight: 700, color: 'var(--text-1)' }}>Total</td>
                <td style={{ textAlign: 'right', fontFamily: 'var(--font-num)', fontWeight: 700 }}>{money(totals.commission)}</td>
                <td style={{ textAlign: 'right', fontFamily: 'var(--font-num)', fontWeight: 700, color: 'var(--success)' }}>{money(totals.paid)}</td>
                <td style={{ textAlign: 'right', fontFamily: 'var(--font-num)', fontWeight: 700 }}>{money(Math.max(0, totals.commission - totals.paid))}</td>
                <td />
              </tr>
            </tfoot>
          )}
        </table>
      </div>

      <RecordBrokerPaymentModal
        invoice={payFor ? {
          id: invoice.id, invoiceNo: invoice.invoiceNo,
          brokerCommissionUSD: payFor.row.commission, brokerPaidUSD: payFor.paid,
          brokerEntityId: payFor.row.brokerEntityId,
        } : null}
        source="sale"
        brokerName={payFor ? entityName(payFor.row.brokerEntityId) : undefined}
        lineBrokerEntityId={payFor?.row.line ? payFor.row.brokerEntityId : undefined}
        onClose={() => setPayFor(null)}
      />
      <EditPaymentModal
        open={!!editing} title="Edit Broker Payment"
        subtitle={editing ? `${entityName(editing.row.brokerEntityId)} · ${fmtDay(editing.pay.paidDate)}` : ''}
        currency={editing?.pay.currency ?? settings.currency}
        form={form} setForm={setForm} saving={saving} error={error}
        onSave={saveEdit} onClose={() => setEditing(null)}
      />
      <ConfirmModal
        open={!!deleting} onClose={() => setDeleting(null)} onConfirm={confirmDelete}
        title="Delete Broker Payment"
        message={deleting ? <>Delete the payment of <strong>{money(deleting.pay.amount ?? 0)}</strong> to {entityName(deleting.row.brokerEntityId)}? The bank balance goes back.</> : null}
        confirmLabel="Delete Payment" loading={delSaving} error={delError}
      />
    </>
  );
}
